import { Routes, UrlSegment } from '@angular/router';
import { SignInComponent } from './user/sign-in.component';
import { HomeComponent } from './home.component';
import { PageNotFoundComponent } from './not-found.component';
import { HubComponent } from './hub/hub.component';
import { authGuardIsSignedIn } from '@aifs-shared/auth/route.guards';
import { TaskIntroComponent } from '@aifs-shared/tracker/task-intro.component';
import { TaskOutroComponent } from '@aifs-shared/tracker/task-outro.component';
import { ContactUsComponent } from './contact-us.component';
import { PublicTermsAndConditionsComponent } from './public-terms-and-conditions.component';
import { PrivacyPolicyComponent } from './privacy-policy.component';
import { ExternalSignInComponent } from './external-sign-in.component';
import { ResetPasswordCompletedState } from '@azure/msal-browser/custom-auth';
import { ResetPasswordComponent } from './user/reset-password.component';
import { ForgotPasswordComponent } from './user/forgot-password.component';

export const routes: Routes = [
    {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
    },
    {
        path: 'e/:token',
        component: ExternalSignInComponent
    },
    {
        path: 'register',
        loadChildren: () => import('./registration/register.routing').then(routes => routes.routes)
    },
    {
        path: 'sign-in',
        component: SignInComponent
    },
    {
        path: 'home',
        component: HomeComponent,
        canActivate: [authGuardIsSignedIn]
    },
    {
        path: 'hub',
        component: HubComponent,
        canActivate: [authGuardIsSignedIn]
    },
    {
        path: 'contact-us',
        component: ContactUsComponent
    },
    {
        path: 'terms-and-conditions',
        component: PublicTermsAndConditionsComponent
    },
    {
        path: 'privacy-policy',
        component: PrivacyPolicyComponent
    },
    {
        path: 'forgot-password',
        component: ForgotPasswordComponent
    },
    {
        path: 'reset-password/:userId',
        component: ResetPasswordComponent
    },
    {
        path: 'application-summary',
        loadChildren: () => import('./application-summary/application-summary.routing').then(routes => routes.routes),
        canActivate: [authGuardIsSignedIn]
    },
    {
        path: 'disclosure',
        loadChildren: () => import('./disclosure/disclosure.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'education',
        loadChildren: () => import('./education/education-work.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'employment',
        loadChildren: () => import('./education/education-work.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'events',
        loadChildren: () => import('./event/event.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'flights',
        loadChildren: () => import('./flight/flight.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'form',
        loadChildren: () => import('./form/form.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'group-cover',
        loadChildren: () => import('./insurance/insurance.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'interview',
        loadChildren: () => import('./interview/interview.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'medical',
        loadChildren: () => import('./medical/medical.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'messages',
        loadChildren: () => import('./messages/message.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'orientation',
        loadChildren: () => import('./orientation/orientation.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'payments',
        loadChildren: () => import('./payments/payments.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'police-check',
        loadChildren: () => import('./police-check/police-check.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'preferences',
        loadChildren: () => import('./preferences/preferences.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'placement',
        loadChildren: () => import('./placement/placement.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'references',
        loadChildren: () => import('./reference/reference.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'self-proposal',
        loadChildren: () => import('./self-proposal/self-proposal.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'skills',
        loadChildren: () => import('./skills/skills.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'uploads',
        loadChildren: () => import('./uploads/uploads.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'user',
        loadChildren: () => import('./user/user.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: 'visa',
        loadChildren: () => import('./visa/visa.routing').then(routes => routes.routes),
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        matcher: (url) => {
            if (url.length > 0 && url[url.length - 1].path.match(/intro/gm)) {
                return {
                    consumed: url,
                    posParams: {
                        taskName: new UrlSegment(url[url.length - 1].path.slice(1), {})
                    }
                };
            }
            return null;
        },
        // path: ':taskName/intro',
        component: TaskIntroComponent,
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        matcher: (url) => {
            if (url.length > 0 && url[url.length - 1].path.match(/finished/gm)) {
                return {
                    consumed: url,
                    posParams: {
                        taskName: new UrlSegment(url[url.length - 1].path.slice(1), {})
                    }
                };
            }
            return null;
        },
        // path: ':taskName/finished',
        component: TaskOutroComponent,
        canActivateChild: [authGuardIsSignedIn]
    },
    {
        path: '**',
        component: PageNotFoundComponent
    }
];
